import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useForecastSession } from 'src/features/forecastSessions/useForecastSession';
import { useForecastSessionSwitch } from 'src/features/forecastSessions/useForecastSessionSwitch';

export const CREATE_SESSION_VALUE = '__create_session__';
export const RENAME_SESSION_VALUE = '__rename_session__';

export function useForecastSessionPicker() {
  const { t } = useTranslation('forecast');
  const { sessions, activeSession, sessionId } = useForecastSession();
  const {
    isCreating,
    isRenaming,
    isSwitching,
    switchSession,
    createAndSwitch,
    renameActiveSession,
  } = useForecastSessionSwitch({ activeSession, sessionId });
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [createSessionName, setCreateSessionName] = useState('');
  const [renameSessionName, setRenameSessionName] = useState('');
  const isBusy = isCreating || isRenaming || isSwitching;

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setCreateSessionName('');
  };

  const closeRenameDialog = () => {
    setIsRenameDialogOpen(false);
    setRenameSessionName('');
  };

  const handleSelectOpenChange = (open: boolean) => {
    if (!isBusy) {
      setIsSelectOpen(open);
    }
  };

  const handleSessionChange = async (value: string) => {
    setIsSelectOpen(false);

    if (value === CREATE_SESSION_VALUE) {
      setIsCreateDialogOpen(true);
      return;
    }

    if (value === RENAME_SESSION_VALUE) {
      if (!activeSession) {
        return;
      }

      setRenameSessionName(activeSession.name);
      setIsRenameDialogOpen(true);
      return;
    }

    await switchSession(value);
  };

  const handleCreateSession = async () => {
    const trimmedName = createSessionName.trim();
    if (!trimmedName) {
      return;
    }

    try {
      await createAndSwitch(trimmedName);
      closeCreateDialog();
      toast.success(t('Success'), {
        description: t('Session created successfully'),
      });
    } catch {
      toast.error(t('Error'), {
        description: t('Failed to create session'),
      });
    }
  };

  const handleRenameSession = async () => {
    const trimmedName = renameSessionName.trim();
    if (!(activeSession && trimmedName)) {
      return;
    }

    try {
      await renameActiveSession(trimmedName);
      closeRenameDialog();
      toast.success(t('Success'), {
        description: t('Session renamed successfully'),
      });
    } catch {
      toast.error(t('Error'), {
        description: t('Failed to rename session'),
      });
    }
  };

  return {
    activeSession,
    sessions,
    isBusy,
    isCreating,
    isRenaming,
    isSelectOpen,
    isCreateDialogOpen,
    isRenameDialogOpen,
    createSessionName,
    renameSessionName,
    setCreateSessionName,
    setRenameSessionName,
    setIsCreateDialogOpen,
    setIsRenameDialogOpen,
    closeCreateDialog,
    closeRenameDialog,
    handleSelectOpenChange,
    handleSessionChange: (value: string) => {
      void handleSessionChange(value);
    },
    handleCreateSession: () => {
      void handleCreateSession();
    },
    handleRenameSession: () => {
      void handleRenameSession();
    },
  };
}
